import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNotify, useRedirect } from 'react-admin';
import {
  SourceRef,
  fetchLinkSource,
  fetchLinkSuggestions,
  fetchLinkTarget,
  linkCaller,
  searchLinkTargets,
  undoCallerLink,
} from './api';
import {
  LinkResult,
  LinkSource,
  LinkTarget,
  PersonChoice,
  RELATIONSHIP_SUGGESTIONS,
  TargetDetail,
  addsCallerAsIs,
  callerLine,
  defaultMoveNumber,
  linkBody,
  linkedMessage,
  personReady,
  prettyPhone,
  targetName,
} from './linkCallerFormat';

const SEARCH_DEBOUNCE_MS = 300;

const EMPTY_CHOICE: PersonChoice = {
  mode: 'new',
  firstName: '',
  lastName: '',
  relationship: '',
  sameAsClientId: null,
};

const KindTag = ({ kind }: { kind: 'funeral' | 'lead' }) => (
  <Box
    component="span"
    sx={{ fontSize: 11, fontWeight: 600, px: 0.75, mr: 1, borderRadius: 1, bgcolor: 'action.hover', color: 'text.secondary' }}
  >
    {kind === 'funeral' ? 'Case' : 'Lead'}
  </Box>
);

/**
 * "Link to…" (T-189): this caller — or this one call — is someone on a case or
 * lead we already have. Pick the case or lead (suggestions, then the ⌘K
 * search), say who they are there (a new family contact with a relationship,
 * or the same person as a contact already on it), and the backend moves the
 * number, the calls and texts, closes the stray lead and leaves a feed note,
 * all in one transaction. Undo is offered right after.
 */
export function LinkCallerDialog({
  source: sourceRef,
  onClose,
  onLinked,
}: {
  /** What is being linked; null keeps the dialog closed. */
  source: SourceRef | null;
  onClose: () => void;
  /** After a link or an undo, so the page can refresh. */
  onLinked?: () => void;
}) {
  const open = !!sourceRef;
  const notify = useNotify();
  const redirect = useRedirect();

  const [src, setSrc] = useState<LinkSource | null>(null);
  const [suggestions, setSuggestions] = useState<LinkTarget[]>([]);
  const [results, setResults] = useState<LinkTarget[]>([]);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState<LinkTarget | null>(null);
  const [detail, setDetail] = useState<TargetDetail | null>(null);
  const [choice, setChoice] = useState<PersonChoice>(EMPTY_CHOICE);
  const [moveNumber, setMoveNumber] = useState(true);
  const [step, setStep] = useState<'find' | 'who' | 'done'>('find');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LinkResult | null>(null);
  const requestId = useRef(0);
  const key = sourceRef ? JSON.stringify(sourceRef) : '';

  // Every open starts clean.
  useEffect(() => {
    if (!sourceRef) return;
    setSrc(null);
    setSuggestions([]);
    setResults([]);
    setSearch('');
    setDebounced('');
    setTarget(null);
    setDetail(null);
    setChoice(EMPTY_CHOICE);
    setStep('find');
    setError(null);
    setResult(null);
    let live = true;
    void fetchLinkSource(sourceRef)
      .then((s) => {
        if (!live) return;
        setSrc(s);
        setMoveNumber(defaultMoveNumber(s));
      })
      .catch((e) => live && setError(e.message));
    void fetchLinkSuggestions(sourceRef)
      .then((s) => live && setSuggestions(s))
      .catch(() => live && setSuggestions([]));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open || !debounced) {
      setResults([]);
      return;
    }
    const current = ++requestId.current;
    setSearching(true);
    void searchLinkTargets(debounced, src?.fromLead?.id)
      .then((r) => current === requestId.current && setResults(r))
      .catch(() => current === requestId.current && setResults([]))
      .finally(() => current === requestId.current && setSearching(false));
  }, [open, debounced, src?.fromLead?.id]);

  const pickTarget = (t: LinkTarget) => {
    setTarget(t);
    setDetail(null);
    setChoice(EMPTY_CHOICE);
    setError(null);
    setStep('who');
    void fetchLinkTarget(t)
      .then((d) => {
        setDetail(d);
        if (!d.canAddContact && src && !addsCallerAsIs(src)) setChoice({ ...EMPTY_CHOICE, mode: 'same' });
      })
      .catch((e) => setError(e.message));
  };

  const handleLink = async () => {
    if (!src || !target) return;
    setBusy(true);
    setError(null);
    try {
      const r = await linkCaller(linkBody(src, target, choice, moveNumber));
      setResult(r);
      setStep('done');
      onLinked?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't link this caller");
    } finally {
      setBusy(false);
    }
  };

  const handleUndo = async () => {
    if (!result) return;
    setBusy(true);
    try {
      await undoCallerLink(result.linkId);
      notify('Link undone — everything is back where it was', { type: 'info' });
      onLinked?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't undo");
    } finally {
      setBusy(false);
    }
  };

  const row = (t: LinkTarget) => (
    <Box
      key={`${t.kind}:${t.id}`}
      role="button"
      tabIndex={0}
      onClick={() => pickTarget(t)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && pickTarget(t)}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        px: 1.5,
        py: 1,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Typography variant="body2" fontWeight={600}>
        <KindTag kind={t.kind} />
        {t.label}
      </Typography>
      {(t.reason || t.sub) && (
        <Typography variant="caption" color="text.secondary" display="block">
          {[t.reason, t.sub].filter(Boolean).join(' · ')}
        </Typography>
      )}
    </Box>
  );

  const phone = prettyPhone(src?.phone);
  const asIs = src ? addsCallerAsIs(src) : false;
  const canLink = !!src && !!target && !!detail && personReady(src, choice) && !busy;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {step === 'done'
          ? 'Linked'
          : src?.call?.channel === 'email'
            ? 'Link this sender to…'
            : src?.call
              ? 'Link this call to…'
              : 'Link to…'}
        {src && (
          <Typography variant="body2" color="text.secondary">
            {callerLine(src)}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        {step === 'find' && (
          <>
            {src?.activeLink && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Already linked to{' '}
                {src.activeLink.funeralId ? `case #${src.activeLink.funeralId}` : 'a lead'}
                {src.activeLink.byName ? ` by ${src.activeLink.byName}` : ''}.
              </Alert>
            )}
            {suggestions.length > 0 && (
              <Box mb={1.5}>
                <Typography variant="overline" color="text.secondary">
                  Suggested
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {suggestions.map(row)}
                </Box>
              </Box>
            )}
            <TextField
              id="link-caller-search"
              autoFocus
              fullWidth
              size="small"
              margin="dense"
              placeholder="Search cases and leads"
              helperText="Deceased or family name, phone, or case number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : undefined,
              }}
            />
            {debounced && !searching && results.length === 0 && (
              <Typography variant="body2" color="text.secondary" py={1.5}>
                Nothing matches &quot;{debounced}&quot;.
              </Typography>
            )}
            {results.length > 0 && (
              <Box display="flex" flexDirection="column" gap={1} mt={1}>
                {results.map(row)}
              </Box>
            )}
          </>
        )}

        {step === 'who' && target && (
          <>
            <Typography variant="body2" fontWeight={600} mb={1}>
              <KindTag kind={target.kind} />
              {detail?.label ?? target.label}
            </Typography>
            {!detail ? (
              <Box py={2} display="flex" justifyContent="center">
                <CircularProgress size={20} />
              </Box>
            ) : (
              <RadioGroup
                value={choice.mode}
                onChange={(e) => setChoice({ ...choice, mode: e.target.value as PersonChoice['mode'] })}
              >
                <FormControlLabel
                  value="new"
                  disabled={!detail.canAddContact && !asIs}
                  control={<Radio size="small" />}
                  label={
                    asIs
                      ? `Add ${src?.fromClient.name} to this ${target.kind === 'funeral' ? 'case' : 'lead'}`
                      : `New family contact on this ${target.kind === 'funeral' ? 'case' : 'lead'}`
                  }
                />
                {!detail.canAddContact && !asIs && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -0.5 }}>
                    This lead has no quote yet, so it can&apos;t take another contact.
                  </Typography>
                )}
                {choice.mode === 'new' && (
                  <Box sx={{ ml: 4, mb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {!asIs && (
                      <Box display="flex" gap={1}>
                        <TextField
                          id="link-caller-first"
                          size="small"
                          label="First name"
                          value={choice.firstName}
                          onChange={(e) => setChoice({ ...choice, firstName: e.target.value })}
                          fullWidth
                        />
                        <TextField
                          id="link-caller-last"
                          size="small"
                          label="Last name"
                          value={choice.lastName}
                          onChange={(e) => setChoice({ ...choice, lastName: e.target.value })}
                          fullWidth
                        />
                      </Box>
                    )}
                    <TextField
                      id="link-caller-relationship"
                      size="small"
                      label="Relationship"
                      placeholder="e.g., Daughter-in-law"
                      value={choice.relationship}
                      onChange={(e) => setChoice({ ...choice, relationship: e.target.value })}
                    />
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {RELATIONSHIP_SUGGESTIONS.map((r) => (
                        <Chip
                          key={r}
                          label={r}
                          size="small"
                          variant={choice.relationship === r ? 'filled' : 'outlined'}
                          onClick={() => setChoice({ ...choice, relationship: r })}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                <FormControlLabel
                  value="same"
                  disabled={detail.contacts.length === 0}
                  control={<Radio size="small" />}
                  label="Same person as…"
                />
                {choice.mode === 'same' && (
                  <RadioGroup
                    sx={{ ml: 4 }}
                    value={choice.sameAsClientId == null ? '' : String(choice.sameAsClientId)}
                    onChange={(e) => setChoice({ ...choice, sameAsClientId: Number(e.target.value) })}
                  >
                    {detail.contacts.map((c) => (
                      <FormControlLabel
                        key={c.clientId}
                        value={String(c.clientId)}
                        control={<Radio size="small" />}
                        label={
                          <span>
                            {c.name}
                            <Typography component="span" variant="caption" color="text.secondary">
                              {[c.relationship, c.phone].filter(Boolean).map((s) => ` · ${s}`).join('')}
                            </Typography>
                          </span>
                        }
                      />
                    ))}
                  </RadioGroup>
                )}
              </RadioGroup>
            )}
            {phone && !(choice.mode === 'new' && asIs) && (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={<Checkbox size="small" checked={moveNumber} onChange={(e) => setMoveNumber(e.target.checked)} />}
                label={
                  <Typography variant="body2">
                    {phone} is theirs — move it to them, so their calls and texts land here
                  </Typography>
                }
              />
            )}
          </>
        )}

        {step === 'done' && result && target && (
          <>
            <Typography variant="body2" paragraph>
              {linkedMessage(result, target, src?.call?.channel)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Feed note: {result.note}
            </Typography>
            {result.warnings.map((w) => (
              <Alert key={w} severity="info" sx={{ mt: 1 }}>
                {w}
              </Alert>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {step === 'find' && (
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
        )}
        {step === 'who' && (
          <>
            <Button onClick={() => setStep('find')} disabled={busy}>
              Back
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => void handleLink()}
              disabled={!canLink}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {target ? `Link to ${targetName(target.kind, target.id)}` : 'Link'}
            </Button>
          </>
        )}
        {step === 'done' && result && (
          <>
            <Button onClick={() => void handleUndo()} disabled={busy} color="inherit">
              Undo
            </Button>
            <Button
              onClick={() => {
                onClose();
                if (result.funeralId) redirect('show', 'funerals', result.funeralId);
                else if (result.leadId) redirect('show', 'leads', result.leadId);
              }}
              disabled={busy}
            >
              Open {result.funeralId ? `case #${result.funeralId}` : 'lead'}
            </Button>
            <Button variant="contained" disableElevation onClick={onClose} disabled={busy}>
              Done
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
